// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Avatar from "@/UI/atoms/Avatar";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyFace = { _id?: string; name: string; picture?: string | null; phone?: string | null; note?: string | null };

type Props = {
    people: TyFace[];
    size?: number;
    /** How many faces before the rest become a count. Four is a card's worth. */
    max?: number;
    /** What to draw when nobody holds it — "unassigned" is a fact worth showing, not a blank. */
    empty?: string;
};

/** Long enough to read a name and a number, short enough that nobody has to dismiss it. */
const LINGER = 3000;

/**
 * Who holds something, as faces rather than a line of names.
 *
 * A list of Arabic full names is the widest thing that ever lands on one of these cards and the
 * least glanceable — three of them truncate, and the truncation lands mid-name. Overlapped avatars
 * say the same thing in a fixed width.
 *
 * **A face answers itself.** Hovering or tapping one opens a small card with the name and the
 * number on it and closes itself after three seconds, which is the whole interaction: the question
 * is always "who is that", it is asked in passing, and a dialog that has to be dismissed costs more
 * than the answer is worth. A `title` would have done the same job for a mouse and nothing at all
 * for the phone this app is opened on during site visits.
 */
export default function AvatarGroup({ people, size = 26, max = 4, empty }: Props) {
    const t = useTranslation();
    const [open, setOpen] = useState<number | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cleared on the way out, or a card unmounted mid-linger leaves a timer writing to nothing.
    useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

    const show = (index: number | null) => {
        if (timer.current) clearTimeout(timer.current);
        setOpen(index);
        if (index !== null) timer.current = setTimeout(() => setOpen(null), LINGER);
    };

    if (!people.length) return empty ? <span className="avatar-group__empty">{empty}</span> : null;

    const shown = people.slice(0, max);
    const hidden = people.length - shown.length;

    return (
        <span className="avatar-group">
            {shown.map((person, index) => (
                <span key={person._id ?? `${person.name}-${index}`} className="avatar-group__face">
                    <button
                        type="button"
                        className="avatar-group__press"
                        onMouseEnter={() => show(index)}
                        onMouseLeave={() => show(null)}
                        onFocus={() => show(index)}
                        onBlur={() => show(null)}
                        onClick={(event) => {
                            // Inside a clickable card: the press is about this face, not the row.
                            event.stopPropagation();
                            show(open === index ? null : index);
                        }}
                        aria-label={person.name}
                    >
                        <Avatar name={person.name} picture={person.picture ?? null} size={size} />
                    </button>

                    {open === index && (
                        <span className="avatar-group__card" role="status">
                            <Avatar name={person.name} picture={person.picture ?? null} size={34} rounded bordered />
                            <span className="avatar-group__text">
                                <span className="avatar-group__name">{person.name}</span>
                                {person.phone && (
                                    <bdi className="avatar-group__phone" dir="ltr">
                                        {person.phone}
                                    </bdi>
                                )}
                                {person.note && <span className="avatar-group__note">{person.note}</span>}
                            </span>
                        </span>
                    )}
                </span>
            ))}

            {hidden > 0 && (
                <span
                    className="avatar-group__rest"
                    style={{ width: size, height: size, fontSize: size * 0.38 }}
                    title={people
                        .slice(max)
                        .map((person) => person.name)
                        .join("، ")}
                >
                    +{hidden}
                </span>
            )}

            <span className="avatar-group__sr">{t.crm.fields.assignees}</span>
        </span>
    );
}
